/**
 * VenueDetailsTab — the Details tab body: identity / address / contact /
 * logistics / capacity / amenities edit sections plus the danger zone in the
 * main column, with the per-field audit trail in the right rail. Write
 * affordances (Edit buttons, danger zone) are suppressed in read-only scope.
 */
import type { VenueDetail, VenuePatch } from '../../../../api/venues';
import { useViewport } from '../../../../hooks/useViewport';

import { AddressSection } from './AddressSection';
import { AuditLog } from './AuditLog';
import { ContactSection } from './ContactSection';
import { DangerZone } from './DangerZone';
import { IdentitySection } from './IdentitySection';
import { BookingPhotoSection } from './photo/BookingPhotoSection';
import type { VenuePhotoFlow } from './photo/use-venue-photo-flow';
import { SalesTaxSection } from './SalesTaxSection';
import { AmenitiesSection, CapacitySection, LogisticsSection } from './sectionsOps';

export interface VenueDetailsTabProps {
  venue: VenueDetail;
  readOnly: boolean;
  photoFlow: VenuePhotoFlow;
  save: (patch: VenuePatch) => Promise<void>;
  onDelete: () => Promise<void>;
  /** Why permanent delete is unavailable, or null when allowed. */
  deleteBlockedReason: string | null;
}

export function VenueDetailsTab({
  venue,
  readOnly,
  photoFlow,
  save,
  onDelete,
  deleteBlockedReason,
}: VenueDetailsTabProps) {
  const sectionProps = { venue, readOnly, save };
  const { isDesktop } = useViewport();
  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: 'grid',
        gridTemplateColumns: isDesktop ? 'minmax(0, 1.55fr) minmax(0, 1fr)' : 'minmax(0, 1fr)',
        gap: 14,
        alignItems: 'start',
      }}
    >
      <section className="nb-card" style={{ minHeight: 0 }}>
        <IdentitySection {...sectionProps} />
        <BookingPhotoSection venue={venue} readOnly={readOnly} flow={photoFlow} />
        <AddressSection {...sectionProps} />
        <ContactSection {...sectionProps} />
        <LogisticsSection {...sectionProps} />
        <CapacitySection {...sectionProps} />
        <SalesTaxSection {...sectionProps} />
        <AmenitiesSection {...sectionProps} />
        {!readOnly && (
          <div style={{ marginTop: 24 }}>
            <DangerZone
              deleteBlockedReason={deleteBlockedReason}
              onDrop={(reason) => save({ stage: 'dropped', droppedReason: reason })}
              onDelete={onDelete}
            />
          </div>
        )}
      </section>

      <section className="nb-card" style={{ minHeight: 0 }}>
        <div className="nb-card-hd">
          <h3>Audit log</h3>
          <span className="nb-eyebrow">per-field</span>
        </div>
        <AuditLog entries={venue.audit ?? []} />
      </section>
    </div>
  );
}
